import { useIsMobileLayout } from '@psl/components/styles/responsive';
import useBehaviorSubject from '@psl/functions/hooks/useBehaviorSubject';
import {
  createContext,
  lazy,
  memo,
  MutableRefObject,
  ReactNode,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { Modal, StyleSheet, View } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import type { OnboardingCompletionHandler } from '../flows/onboarding/functions/useOnboardingProgress';
import { CurrentShow } from '../flows/showcase/functions/useCurrentShow/useCurrentShow';
import { useCreation } from '../functions/hooks/useCreation';

const Confetti = lazy(() => import('@psl/components/confetti/Confetti'));

export type CoursePurchasedEvent = {
  userId: string;
  listingId: string;
  orderId: string;
};

export type AppContextProps = {
  currentShow$: BehaviorSubject<
    (CurrentShow & { isNested?: boolean }) | undefined
  >;
  currentProfileId$: BehaviorSubject<string | undefined>;
  onboardingCompletionHandler: MutableRefObject<
    OnboardingCompletionHandler | undefined
  >;
  isAsideActive$: BehaviorSubject<boolean>;
  showConfetti: (completionHandler?: () => void) => void;
};

export const AppContext = createContext<AppContextProps>(null as any);

export const useAppContext = () => useContext(AppContext);

export const AppContextProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const isMobile = useIsMobileLayout();
    const onboardingCompletionHandler = useRef<OnboardingCompletionHandler>();
    const currentShow$ = useCreation(
      () => new BehaviorSubject<CurrentShow | undefined>(undefined),
      [],
    );
    const currentProfileId$ = useCreation(
      () => new BehaviorSubject<string | undefined>(undefined),
      [],
    );

    const isAsideActive$ = useCreation(() => new BehaviorSubject(false), []);
    const showConfetti$ = useCreation(() => new BehaviorSubject(false), []);

    const [confettiVisible] = useBehaviorSubject(showConfetti$);
    const confettiCompletionHandler = useRef<() => void>();

    const showConfetti = useCallback(
      (completionHandler?: () => void) => {
        showConfetti$.next(true);
        confettiCompletionHandler.current = completionHandler;
      },
      [showConfetti$],
    );

    const value = useMemo(
      () => ({
        currentShow$,
        currentProfileId$,
        isAsideActive$,
        onboardingCompletionHandler,
        showConfetti,
      }),
      [currentShow$, currentProfileId$, isAsideActive$, showConfetti],
    );

    const onConfettiFinish = useCallback(() => {
      showConfetti$.next(false);
      confettiCompletionHandler.current?.();
      confettiCompletionHandler.current = undefined;
    }, [showConfetti$]);

    return (
      <AppContext.Provider value={value}>
        {children}
        {confettiVisible && (
          <Suspense fallback={null}>
            <Modal
              transparent
              animationType="none"
              visible={confettiVisible}
              pointerEvents="none"
            >
              <View
                style={[
                  StyleSheet.absoluteFill,
                  { zIndex: 10, transform: [{ scale: isMobile ? 2.5 : 1.5 }] },
                ]}
                pointerEvents="none"
                nativeID="confetti"
              >
                <Confetti
                  loop={false}
                  style={[StyleSheet.absoluteFill]}
                  onAnimationFinish={onConfettiFinish}
                />
              </View>
            </Modal>
          </Suspense>
        )}
      </AppContext.Provider>
    );
  },
);
